import { ReactNode } from 'react'
import styles from './ToggleInput.module.scss'

interface ToggleOption<T extends string | boolean> {
    value: T
    label: string
}

interface ToggleInputProps<T extends string | boolean> {
    label?: string
    value: T | null
    options?: ToggleOption<T>[]
    onChange: (value: T) => void
    required?: boolean
    labelRight?: ReactNode
    /** Accessible name when no visible label is shown. */
    ariaLabel?: string
    /** Size pills to their label instead of stretching across the row. */
    fit?: boolean
    /** Taller pills, for form questions rather than compact filters. */
    spacious?: boolean
}

const DEFAULT_OPTIONS: [ToggleOption<boolean>, ToggleOption<boolean>] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
]

export const ToggleInput = <T extends string | boolean>({
    label,
    value,
    options,
    onChange,
    required = false,
    labelRight,
    ariaLabel,
    fit = false,
    spacious = false,
}: ToggleInputProps<T>) => {
    const opts = (options ?? DEFAULT_OPTIONS) as [ToggleOption<T>, ToggleOption<T>]

    return (
        <div className={styles.field}>
            {(label || labelRight) && (
                <span className={styles.label}>
                    {label && (
                        <span>
                            {label}
                            {required && <span className={styles.req}>*</span>}
                        </span>
                    )}
                    {labelRight && <span className={styles.labelRight}>{labelRight}</span>}
                </span>
            )}
            <div className={styles.buttons} role="group" aria-label={ariaLabel || label || undefined}>
                {opts.map((opt) => (
                    <button
                        key={String(opt.value)}
                        type="button"
                        className={`${styles.toggle} ${fit ? styles.fit : ''} ${spacious ? styles.spacious : ''} ${value === opt.value ? styles.selected : ''}`}
                        aria-pressed={value === opt.value}
                        onClick={() => onChange(opt.value)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    )
}
